<script setup lang="ts">
import { computed, useAttrs } from 'vue'
import BaseImage from '@/components/ui/BaseImage.vue'
import CoreCard from '@/components/core/CoreCard.vue'
import TagList from '@/components/ui/TagList.vue'
import { formatCompact3 } from '@/utils/stringFormatting'
import { parseDescriptionMarkdown } from '@/utils/markdown'
import HeartIcon from 'vue-material-design-icons/Heart.vue'
import ChartBarIcon from 'vue-material-design-icons/ChartBar.vue'
import MessageTextIcon from 'vue-material-design-icons/MessageText.vue'
import type { ImageFocus } from '@/lib/media/imageAssetFields'

defineOptions({ inheritAttrs: false })

type StatsFallback = {
  messageCountTotal: number
  avgMessagesPerConversation: number
}

type CharacterCardProps = {
  title: string
  tagline?: string
  imageSrc?: string
  imageAlt?: string
  imageFocus?: ImageFocus | null
  tags?: readonly unknown[]
  description?: string
  favorited?: boolean
  numberFavorites?: number | null
  messageCountTotal?: number | null
  avgMessagesPerConversation?: number | null
  statsFallback?: StatsFallback
  disabled?: boolean
  loading?: boolean
  taglineLineClamp?: number
}

const props = withDefaults(defineProps<CharacterCardProps>(), {
  tagline: '',
  imageAlt: '',
  imageFocus: null,
  tags: () => [],
  description: '',
  favorited: false,
  numberFavorites: null,
  messageCountTotal: null,
  avgMessagesPerConversation: null,
  statsFallback: () => ({ messageCountTotal: 1234, avgMessagesPerConversation: 12 }),
  disabled: false,
  loading: false,
  taglineLineClamp: 6,
})

type CharacterCardEmits = {
  (e: 'click', event: MouseEvent): void
}

const emit = defineEmits<CharacterCardEmits>()

const attrs = useAttrs()

const effectiveMessageCountTotal = computed(() => {
  if (typeof props.messageCountTotal === 'number' && Number.isFinite(props.messageCountTotal))
    return props.messageCountTotal
  return props.statsFallback.messageCountTotal
})

const effectiveAvgMessagesPerConversation = computed(() => {
  if (
    typeof props.avgMessagesPerConversation === 'number' &&
    Number.isFinite(props.avgMessagesPerConversation)
  ) {
    return props.avgMessagesPerConversation
  }
  return props.statsFallback.avgMessagesPerConversation
})

const formattedMessageCountTotal = computed(() => formatCompact3(effectiveMessageCountTotal.value))
const formattedAvgMessagesPerConversation = computed(() => {
  const value = effectiveAvgMessagesPerConversation.value
  if (Math.abs(value) < 1000) return String(Math.round(value))
  return formatCompact3(value)
})

const formattedNumberFavorites = computed(() => {
  if (props.numberFavorites == null) return null
  const value = Number(props.numberFavorites)
  if (!Number.isFinite(value)) return null
  return formatCompact3(Math.max(0, value))
})

const effectiveTagline = computed(() => props.tagline || props.description || '')
const taglineClampStyle = computed(() => ({
  '-webkit-line-clamp': String(Math.max(1, props.taglineLineClamp)),
}))
const renderedTagline = computed(() => parseDescriptionMarkdown(effectiveTagline.value, 'compact'))
</script>

<template>
  <CoreCard
    v-bind="attrs"
    as="div"
    variant="surface"
    density="compact"
    rounded="md"
    :hover="!disabled && !loading"
    :disabled="disabled"
    :loading="loading"
    class="char-card w-full text-left overflow-hidden flex flex-col items-stretch justify-start cursor-pointer"
    :aria-label="title"
    @click="emit('click', $event)"
  >
    <template #media>
      <div class="-m-3 mb-3 relative overflow-hidden rounded-t-card">
        <div class="char-card__portrait-wrap">
          <BaseImage
            :src="imageSrc"
            :alt="imageAlt"
            :image-focus="imageFocus"
            fallback-size="lg"
            class="char-card__portrait block w-full h-full object-cover"
          />
        </div>

        <div class="char-card__stats-overlay" aria-hidden="true">
          <span class="char-card__stat">
            <MessageTextIcon :size="13" />
            {{ formattedMessageCountTotal }}
          </span>
          <span class="char-card__stat">
            <ChartBarIcon :size="13" />
            {{ formattedAvgMessagesPerConversation }}
          </span>
          <span v-if="formattedNumberFavorites != null" class="char-card__stat">
            <HeartIcon :size="13" />
            {{ formattedNumberFavorites }}
          </span>
        </div>

        <div v-if="favorited" class="absolute top-2 right-2 text-rose-500 p-1">
          <slot name="favorite" :favorited="favorited">
            <HeartIcon title="Favorited" :size="18" :decorative="false" class="drop-shadow-lg" />
          </slot>
        </div>
      </div>
    </template>

    <div class="char-card__body">
      <h3 class="char-card__name">
        <slot name="title">{{ title }}</slot>
      </h3>
      <div
        v-if="effectiveTagline"
        class="char-card__tagline"
        :style="taglineClampStyle"
        v-html="renderedTagline"
      />

      <slot name="tags" :tags="tags">
        <TagList v-if="tags && tags.length" :tags="(tags as string[])" class="char-card__tags" />
      </slot>
    </div>
  </CoreCard>
</template>

<style scoped>
.char-card__portrait-wrap {
  aspect-ratio: 3 / 4;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  background: rgba(9, 12, 16, 0.6);
}

.char-card__portrait {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 300ms ease;
}

.char-card:hover .char-card__portrait {
  transform: scale(1.04);
}

.char-card__stats-overlay {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 24px 10px 8px;
  background: linear-gradient(to top, rgba(9, 12, 16, 0.88) 0%, transparent 100%);
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  opacity: 0;
  transition: opacity 200ms;
}

.char-card:hover .char-card__stats-overlay {
  opacity: 1;
}

@media (hover: none) {
  .char-card__stats-overlay {
    display: none;
  }
}

.char-card__stat {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  font-size: 0.72rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.9);
  background: rgba(9, 12, 16, 0.55);
  border-radius: 4px;
  padding: 2px 6px;
}

.char-card__body {
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
}

.char-card__name {
  font-size: 0.9rem;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.92);
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.char-card__tagline {
  font-size: 0.75rem;
  line-height: 1.4;
  color: rgba(255, 255, 255, 0.45);
  margin: 0;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  overflow: hidden;
  flex: 1;
}

.char-card__tagline :deep(strong),
.char-card__tagline :deep(em),
.char-card__tagline :deep(code),
.char-card__tagline :deep(a) {
  color: inherit;
}

.char-card__tagline :deep(code) {
  font-size: 0.72rem;
}

.char-card__tagline :deep(a) {
  text-decoration: underline;
}

.char-card__tags {
  margin-top: 2px;
}
</style>
